import { useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, TrendingUp } from "lucide-react";

const SUB_MONTHLY = 100;
const feePerUnit = (price) => 0.005 * price;

const STAGES = [
  { key: "launch", label: "Launch", sub: "first weeks", range: [1, 3] },
  { key: "growing", label: "Growing", sub: "early traction", range: [3, 15] },
  { key: "established", label: "Established", sub: "scaled traffic", range: [20, 100] },
];

export default function EarningsEstimator() {
  const [price, setPrice] = useState(25);

  const p = Math.max(0, Number(price) || 0);

  return (
    <div data-testid="earnings-estimator" className="rounded-xl border border-white/10 bg-white/[0.03] p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-md bg-[#007AFF]/10 border border-[#007AFF]/30 inline-flex items-center justify-center">
          <Calculator className="h-4 w-4 text-[#007AFF]" />
        </span>
        <div>
          <h2 className="font-black text-xl tracking-tight">Seller earnings estimator</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Honest projections, not promises.</p>
        </div>
      </div>

      <div className="mt-6 max-w-xs">
        <label className="text-xs uppercase tracking-widest text-neutral-400">Your product price (USD)</label>
        <div className="mt-2 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-mono">$</span>
          <input
            type="number"
            min="0"
            step="1"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="estimator-price-input"
            className="w-full h-11 rounded-md bg-[#121212] border border-[#27272A] pl-7 pr-3 font-mono text-lg text-white focus:outline-none focus:border-[#007AFF]"
          />
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        {STAGES.map((s) => {
          const [lo, hi] = s.range;
          const mid = (lo + hi) / 2;
          const grossMonth = p * mid * 30;
          const vismarkCosts = feePerUnit(p) * mid * 30 + SUB_MONTHLY;
          const net = Math.max(0, grossMonth - vismarkCosts);
          return (
            <div key={s.key} data-testid={`estimator-stage-${s.key}`} className="rounded-lg border border-white/10 bg-[#121212] p-5">
              <div className="flex items-center justify-between">
                <div className="font-bold text-white">{s.label}</div>
                <TrendingUp className="h-3.5 w-3.5 text-neutral-600" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{s.sub}</div>
              <dl className="mt-4 space-y-2.5 text-sm">
                <Row label="Est. sales / day" value={`${lo}–${hi}`} />
                <Row label="Gross / month" value={`$${fmt(grossMonth)}`} />
                <Row label="Vismarks costs / mo" value={`−$${fmt(vismarkCosts)}`} muted />
                <div className="pt-2 border-t border-white/5">
                  <Row label="Net before product costs" value={`$${fmt(net)}`} strong />
                </div>
              </dl>
            </div>
          );
        })}
      </div>

      <p className="mt-5 text-[11px] text-neutral-500 leading-relaxed">
        Based on a ${SUB_MONTHLY}/mo subscription + a 0.5% fee per item sold and typical marketplace
        conversion at each traffic stage. <span className="text-neutral-400">Estimates only — not a guarantee of
        sales.</span> Results depend on demand, pricing, and product quality.{" "}
        <Link to="/legal" data-testid="estimator-legal-link" className="text-[#007AFF] hover:underline">Legal Notice</Link>
      </p>
    </div>
  );
}

function fmt(n) {
  return Math.round(n).toLocaleString();
}

function Row({ label, value, muted, strong }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={`text-xs ${muted ? "text-neutral-600" : "text-neutral-500"}`}>{label}</dt>
      <dd className={`font-mono tabular-nums ${strong ? "font-bold text-[#34C759]" : muted ? "text-neutral-400" : "text-white"}`}>{value}</dd>
    </div>
  );
}
